import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Share, Plus, Smartphone } from "lucide-react";
import { logError } from "@/lib/log";

/**
 * InstallAppButton — appears in the Driver Day header.
 * - On Android Chrome: triggers the native install prompt via `beforeinstallprompt`
 * - On iPhone Safari: opens a small dialog with the "Share → Add to Home Screen" instructions
 * - When the app is already installed (running in standalone display-mode), the button hides itself
 */
export default function InstallAppButton({
  manifest = null,
  appleIcon = null,
  appName = "Catskills Pool Supplies",
  iosTitle = null,
  allowDesktop = false,
  className = "",
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  // Point the page at the right manifest (worker/desktop/management get
  // their own installable app identity instead of the Driver one).
  useEffect(() => {
    if (!manifest) return;
    const link = document.querySelector("link[rel='manifest']");
    const prev = link?.getAttribute("href");
    link?.setAttribute("href", manifest);
    const apple = document.querySelector("link[rel='apple-touch-icon']");
    const prevApple = apple?.getAttribute("href");
    if (appleIcon) apple?.setAttribute("href", appleIcon);
    // iOS names the Home-Screen shortcut from this meta (falls back to <title>).
    let titleMeta = document.querySelector("meta[name='apple-mobile-web-app-title']");
    const createdMeta = !titleMeta;
    if (!titleMeta) {
      titleMeta = document.createElement("meta");
      titleMeta.setAttribute("name", "apple-mobile-web-app-title");
      document.head.appendChild(titleMeta);
    }
    const prevTitle = titleMeta.getAttribute("content");
    if (iosTitle) titleMeta.setAttribute("content", iosTitle);
    return () => {
      if (prev) link?.setAttribute("href", prev);
      if (appleIcon && prevApple) apple?.setAttribute("href", prevApple);
      if (createdMeta) titleMeta.remove();
      else if (prevTitle) titleMeta.setAttribute("content", prevTitle);
    };
  }, [manifest, appleIcon, iosTitle]);

  useEffect(() => {
    // Detect if already installed and running as standalone PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true; // iOS Safari flag
    setStandalone(isStandalone);

    const handler = (e) => {
      // Chrome / Edge / Android: capture the install prompt so we can fire it on our own button click
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const onInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone) return null;

  // Only show on phones/tablets — hidden on PC/desktop browsers unless
  // the caller opts in (the /desktop workspace is used on PCs too).
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isMobile = isIos || /Android|Mobi/i.test(navigator.userAgent);
  if (!isMobile && !allowDesktop) return null;

  const onClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch (e) { logError("Install prompt failed:", e); }
      setDeferredPrompt(null);
      return;
    }
    if (isIos) {
      setShowIosHelp(true);
      return;
    }
    // Desktop / unsupported: tell user to use browser menu
    setShowIosHelp(true);
  };

  return (
    <>
      <Button
        type="button"
        onClick={onClick}
        variant="outline"
        size="sm"
        className={`rounded-sm h-10 px-3 ${className}`}
        data-testid="install-app-button"
      >
        <Download className="w-4 h-4 mr-2" /> Install app
      </Button>

      <Dialog open={showIosHelp} onOpenChange={setShowIosHelp}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="install-app-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-primary" /> Install on your device
            </DialogTitle>
            <DialogDescription>
              Add {appName} as an app icon so it opens like a regular app.
            </DialogDescription>
          </DialogHeader>

          {isIos ? (
            <ol className="space-y-3 text-sm py-2">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <span>Tap the <Share className="inline w-4 h-4 -mt-0.5 mx-0.5" /> <strong>Share</strong> button at the bottom of Safari</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <span>Scroll down and tap <Plus className="inline w-4 h-4 -mt-0.5 mx-0.5" /> <strong>Add to Home Screen</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <span>Tap <strong>Add</strong> in the top right — done!</span>
              </li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm py-2">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <span>Open the browser menu (the three-dot icon in Chrome)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold inline-flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <span>Confirm — the app icon will appear on your home screen</span>
              </li>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
